import type { FlowDefinition, NodeDefinition, TransitionDefinition } from "@docit/flow-editor";

/**
 * Pure operations on a flow definition. No React, no network — so the rules that protect an
 * author's work can be tested without rendering anything.
 *
 * The invariant every function here exists to keep (W3 §F-10): **never construct a node or the
 * root from scratch.** The server holds the definition as a raw JSON object and patches it in
 * place, so fields slate has never heard of — `overseer`, `temperature`, `human`, `work`,
 * `permissions`, whatever lands next — round-trip verbatim. Every edit below is a spread over
 * what was already there. An editor that rebuilds its document deletes the parts it does not
 * understand, silently, on the author's behalf.
 */

/**
 * The client mirror of `AiFlow.Normalize`. Ported verbatim because the slug becomes a FILE NAME
 * in the `.tomato/flows` mirror — a client that normalizes differently invents a second flow.
 */
export function slugFromName(name: string): string {
  let slug = Array.from(name.trim())
    .map((char) => (/[\p{L}\p{N}\-_.]/u.test(char) ? char : "-"))
    .join("");
  // Collapse repeatedly: "a...b" must become "a.b", not "a..b".
  let previous = "";
  while (previous !== slug) {
    previous = slug;
    slug = slug.replace(/\.\./g, ".").replace(/--/g, "-");
  }
  slug = slug.replace(/^[-._]+/, "").replace(/[-._]+$/, "");
  return slug === "" ? "flow" : slug.toLowerCase();
}

/** A slug nothing else in the workspace is using — `name`, `name-2`, `name-3`… */
export function freeSlug(name: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base = slugFromName(name);
  if (!used.has(base)) {
    return base;
  }
  let n = 2;
  while (used.has(`${base}-${n}`)) {
    n += 1;
  }
  return `${base}-${n}`;
}

/**
 * Overlay the canvas's STRUCTURE onto the host's draft (W3 §D.4).
 *
 * The canvas keeps its own copy from the last hydration, so `merged` is authoritative only
 * about which nodes exist, their transitions and the layout. Content — instructions, models,
 * budgets — is read from the draft, which is where the inspectors write. Doing this the other
 * way round is exactly how a two-editor screen loses an author's prompt.
 */
export function applyCanvasStructure(
  draft: FlowDefinition,
  merged: FlowDefinition,
): FlowDefinition {
  const byId = new Map((draft.nodes ?? []).map((node) => [node.nodeId, node]));
  const nodes = (merged.nodes ?? []).map((incoming) => {
    const base = byId.get(incoming.nodeId);
    // A node the canvas has just created has no counterpart in the draft — take it whole.
    return base ? { ...base, transitions: incoming.transitions ?? [] } : incoming;
  });
  return { ...draft, nodes, layout: { ...(draft.layout ?? {}), ...(merged.layout ?? {}) } };
}

/** Root-level patch. Keys set to `undefined` are REMOVED, so a cleared field leaves no `""`. */
export function patchFlow(draft: FlowDefinition, patch: Record<string, unknown>): FlowDefinition {
  const next: FlowDefinition = { ...draft, ...patch };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      delete next[key];
    }
  }
  return next;
}

/** Patch one node, leaving every other node — and every field slate does not render — alone. */
export function patchNode(
  draft: FlowDefinition,
  nodeId: string,
  patch: Record<string, unknown>,
): FlowDefinition {
  const nodes = (draft.nodes ?? []).map((node) => {
    if (node.nodeId !== nodeId) {
      return node;
    }
    const next: NodeDefinition = { ...node, ...patch };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined) {
        delete next[key];
      }
    }
    return next;
  });
  return { ...draft, nodes };
}

export function findNode(draft: FlowDefinition, nodeId: string | null): NodeDefinition | undefined {
  return nodeId ? (draft.nodes ?? []).find((node) => node.nodeId === nodeId) : undefined;
}

/** Replace one node's transition list wholesale — the inspector edits it as an ordered list. */
export function setTransitions(
  draft: FlowDefinition,
  nodeId: string,
  transitions: TransitionDefinition[],
): FlowDefinition {
  return patchNode(draft, nodeId, { transitions });
}

/**
 * Resolve the canvas's edge id back to the transition it names.
 *
 * The id is `` `${fromNodeId}->${nextNodeId}:${condition}` ``, and conditions CONTAIN COLONS
 * (`contains(prev.result, "VERDICT: THIN")`). Splitting on every colon finds the wrong edge —
 * so: locate `->`, then the FIRST colon after it, and take everything past it as the condition.
 */
export function parseEdgeId(
  edgeId: string,
): { fromNodeId: string; nextNodeId: string; condition: string } | null {
  const arrow = edgeId.indexOf("->");
  if (arrow < 0) {
    return null;
  }
  const colon = edgeId.indexOf(":", arrow + 2);
  if (colon < 0) {
    return null;
  }
  return {
    fromNodeId: edgeId.slice(0, arrow),
    nextNodeId: edgeId.slice(arrow + 2, colon),
    condition: edgeId.slice(colon + 1),
  };
}

/** The selected edge as `{node, index}`, or null when the id names nothing that still exists. */
export function locateTransition(
  draft: FlowDefinition,
  edgeId: string | null,
): { node: NodeDefinition; index: number; transition: TransitionDefinition } | null {
  if (!edgeId) {
    return null;
  }
  const parsed = parseEdgeId(edgeId);
  if (!parsed) {
    return null;
  }
  const node = findNode(draft, parsed.fromNodeId);
  if (!node) {
    return null;
  }
  const index = (node.transitions ?? []).findIndex(
    (transition) =>
      (transition.nextNodeId ?? "") === parsed.nextNodeId &&
      (transition.condition ?? "") === parsed.condition,
  );
  const transition = node.transitions?.[index];
  return index < 0 || !transition ? null : { node, index, transition };
}

/** Move an item inside an ordered list. Order IS the routing rule: first match wins. */
export function reorder<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) {
    return items;
  }
  next.splice(to, 0, moved);
  return next;
}

/** A brand-new flow: one node, named, reachable and dirty. Saving it is what creates it. */
export function scaffoldFlow(name: string, slug: string): FlowDefinition {
  return {
    id: slug,
    name,
    description: "",
    startNodeId: "start",
    nodes: [
      {
        nodeId: "start",
        name: "First step",
        ownerRole: "ide:claude",
        toolName: "NoOpTool",
        instructions: "",
        transitions: [],
      },
    ],
    layout: { start: { x: 80, y: 80 } },
  };
}

/**
 * A copy of `source` under a new identity. Everything else — nodes, sources, layout, output,
 * and whatever slate does not render — comes along, because a duplicate that quietly drops
 * fields is worse than no duplicate at all.
 */
export function duplicateFlow(source: FlowDefinition, slug: string, name: string): FlowDefinition {
  return { ...structuredClone(source), id: slug, name };
}

/** A source id the author cannot break a reference with: `sources.<id>` must stay a token. */
export function sanitizeSourceId(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, "");
}

/** The next free `sourceN`, so adding two sources in a row does not collide. */
export function nextSourceId(draft: FlowDefinition): string {
  const used = new Set((draft.sources ?? []).map((source) => source.sourceId));
  let n = 1;
  while (used.has(`source${n}`)) {
    n += 1;
  }
  return `source${n}`;
}

/** Split a textarea the author fills one-per-line into the string[] the definition stores. */
export function parseLines(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function formatLines(value: unknown): string {
  return Array.isArray(value) ? value.filter((entry) => typeof entry === "string").join("\n") : "";
}

/** `undefined` for a blank number field — the definition should carry no key, not a NaN. */
export function parseNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return undefined;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}
